<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <header v-if="isAdmin" class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
      <div class="flex flex-wrap justify-between items-center gap-2 max-w-7xl mx-auto">
        <h1 class="text-xl font-semibold text-indigo-300">จัดการวิญญาณ</h1>
        <div class="flex flex-wrap items-center gap-2">
          <TemplateExportButton
            collection-name="gameGhosts"
            file-name="gameGhosts.json"
            label="Export gameGhosts.json"
          />
        <button 
          @click="toggleModal = 'AddGhostModal'" 
          class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="สร้างวิญญาณใหม่"
        >
          <i class="fas fa-plus-circle text-xs"></i> สร้างวิญญาณ
        </button>
        </div>
      </div>
    </header>

    <!-- Filters -->
    <section class="w-full">
      <div class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
        <h3 class="text-sm font-semibold text-indigo-200 mb-3">ตัวกรองวิญญาณ</h3>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <!-- Level (Required) -->
          <div>
            <label class="block text-sm font-medium text-indigo-200 mb-1">เลือกเลเวล (จำเป็น)</label>
            <select 
              v-model="filterLevel" 
              @change="applyFilters" 
              class="w-full px-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              aria-label="เลือกเลเวลวิญญาณ"
            >
              <option value="">เลือกเลเวล</option>
              <option v-for="level in [30, 31,32,33,34,35,36,37,38,39,40,41,42,43,44]" :key="level" :value="level">{{ level }}</option>
            </select>
          </div>

          <!-- Type -->
          <div>
            <label class="block text-sm font-medium text-indigo-200 mb-1">ประเภทวิญญาณ</label>
            <select 
              v-model="filterType" 
              @change="applyFilters" 
              class="w-full px-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              :disabled="!filterLevel"
              aria-label="ประเภทวิญญาณ"
            >
              <option value="">ทั้งหมด</option>
              <option value="normal">ทั่วไป</option>
              <option value="boss">บอส</option>
            </select>
          </div>

          <!-- Name Search -->
          <div class="relative">
            <label class="block text-sm font-medium text-indigo-200 mb-1">ค้นหาชื่อวิญญาณ</label>
            <i class="fas fa-magnifying-glass absolute left-3 top-10 text-indigo-400"></i>
            <input 
              v-model="filterName" 
              @input="debouncedApplyFilters" 
              type="text" 
              placeholder="ค้นหาชื่อวิญญาณ..." 
              class="w-full pl-10 pr-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              :disabled="!filterLevel"
              aria-label="ค้นหาชื่อวิญญาณ"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto">
      <!-- Error Message -->
      <div v-if="error" class="text-red-300 text-sm text-center mb-4">
        {{ error }}
      </div>

      <!-- Prompt to Select Level -->
      <div v-if="!filterLevel" class="text-center text-indigo-200 py-6">
        <i class="fas fa-skull-crossbones text-2xl mb-2"></i>
        <p class="text-sm">กรุณาเลือกเลเวลวิญญาณเพื่อแสดงรายการ</p>
      </div>

      <div v-else>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div 
            v-for="ghost in displayedGhosts" 
            :key="ghost.id" 
            @click="openDetailsModal(ghost)"
            class="bg-gray-900 rounded-md p-4 border border-gray-800 bg-opacity-80 hover:bg-opacity-80 transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer shadow-xl"
          >
            <!-- Header -->
            <div class="flex items-center space-x-3 mb-3">
              <img 
                :src="ghost.imageUrl || 'default-ghost.png'" 
                alt="Ghost Image" 
                class="w-16 h-16 object-cover rounded-md border-2"
                :class="ghost.rarityColors?.borderColor || 'border-indigo-400'"
                @error="handleImageError"
              />
              <div>
                <h3 
                  class="text-sm font-semibold"
                  :class="ghost.rarityColors?.textColor || 'text-indigo-200'"
                >
                  {{ ghost.name }}
                </h3>
                <p class="text-xs text-indigo-200">เลเวล {{ ghost.level }} ({{ ghost.type === 'boss' ? 'บอส' : 'ทั่วไป' }})</p>
              </div>
            </div>

            <!-- Stats -->
            <div class="grid grid-cols-2 gap-2 text-xs border-t border-gray-600 pt-3 mb-3">
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-heart text-red-400"></i> Stamina: {{ ghost.stats.stamina }}
              </p>
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-dumbbell text-yellow-400"></i> Str: {{ ghost.stats.str }}
              </p>
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-brain text-purple-400"></i> Int: {{ ghost.stats.int }}
              </p>
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-running text-green-400"></i> Agi: {{ ghost.stats.agi }}
              </p>
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-shield-alt text-blue-400"></i> Def: {{ ghost.stats.defense }}
              </p>
              <p class="text-indigo-200 flex items-center gap-1">
                <i class="fas fa-clover text-lime-400"></i> Luck: {{ ghost.stats.luck }}
              </p>
            </div>

            <!-- Edit Button -->
            <div v-if="isAdmin" class="mt-3">
              <button 
                @click.stop="openEditModal(ghost)" 
                class="w-full bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-3 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="แก้ไขวิญญาณ"
              >
                <i class="fas fa-edit text-xs"></i> แก้ไข
              </button>
            </div>
          </div>
        </div>

        <!-- No Ghosts Message -->
        <div v-if="displayedGhosts.length === 0 && !isLoading && filterLevel" class="text-center text-indigo-200 py-6">
          <i class="fas fa-skull-crossbones text-2xl mb-2"></i>
          <p class="text-sm">ไม่พบวิญญาณที่ตรงกับการค้นหา</p>
        </div>

        <!-- Load More Button -->
        <div class="mt-6 flex justify-center">
          <button 
            v-if="hasMore && !isLoading" 
            @click="loadMore" 
            class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="โหลดเพิ่มเติม"
          >
            <i class="fas fa-arrow-down text-xs"></i> โหลดเพิ่มเติม
          </button>
          <div v-if="isLoading" class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
          </div>
          <span v-if="!hasMore && displayedGhosts.length > 0" class="text-indigo-200 text-sm">ไม่มีวิญญาณเพิ่มเติมแล้ว</span>
        </div>
      </div>
    </main>

    <!-- Modals -->
    <AddGhostModal 
      v-if="toggleModal === 'AddGhostModal'" 
      @cancelModal="toggleModal = ''" 
      @ghostAdded="resetAndFetch" 
    />
    <EditGhostModal 
      v-if="toggleModal === 'EditGhostModal'" 
      :ghost="selectedGhost" 
      @cancelModal="toggleModal = ''" 
      @ghostUpdated="resetAndFetch" 
    />
    <GhostDetailsModal 
      v-if="toggleModal === 'GhostDetailsModal'" 
      :ghost="selectedGhost" 
      @cancelModal="toggleModal = ''" 
      @editGhost="openEditModal(selectedGhost)"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import AddGhostModal from '../components/admin/AddGhostModal.vue';
import EditGhostModal from '../components/admin/EditGhostModal.vue';
import GhostDetailsModal from '../components/admin/GhostDetailsModal.vue';
import TemplateExportButton from '../components/admin/TemplateExportButton.vue';
import gameGhostsData from '@/data/gameGhosts.json';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';

export default {
  components: { AddGhostModal, EditGhostModal, GhostDetailsModal, TemplateExportButton },
  setup() {
    const toggleModal = ref('');
    const ghosts = ref([]);
    const filterName = ref('');
    const filterLevel = ref('');
    const filterType = ref('');
    const selectedGhost = ref(null);
    const lastVisible = ref(null);
    const hasMore = ref(true);
    const isLoading = ref(false);
    const isAdmin = ref(false);
    const error = ref('');
    const ghostsPerLoad = 30;
    const batchSize = 100; // Fetch more for name search
    const currentPage = ref(1);

    const { user } = getUser();

    // Debounce function for name search
    const debounce = (func, wait) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
      };
    };

    // Handle image load errors
    const handleImageError = (event) => {
      event.target.src = 'default-ghost.png';
    };

    const playerStore = usePlayerStore();

    watch(() => playerStore.userData, (data) => {
      if (data) {
        isAdmin.value = data.admin === true;
      } else {
        isAdmin.value = false;
      }
    }, { immediate: true });

    const getTime = (createdAt) => {
      if (!createdAt) return 0;
      if (createdAt.toDate) return createdAt.toDate().getTime();
      if (createdAt.seconds) return createdAt.seconds * 1000;
      return new Date(createdAt).getTime();
    };

    // Fetch ghosts from JSON with filters
    const fetchGhosts = async (isLoadMore = false, reset = false) => {
      if (!filterLevel.value) {
        ghosts.value = [];
        hasMore.value = false;
        isLoading.value = false;
        return;
      }

      try {
        isLoading.value = true;
        error.value = '';

        let newGhosts = Object.entries(gameGhostsData).map(([id, data]) => {
          if (!data.rarityColors) {
            data.rarityColors = {
              bgColor: 'bg-gray-700',
              textColor: 'text-indigo-200',
              borderColor: 'border-indigo-400',
            };
          }
          return { id, ...data };
        });

        newGhosts = newGhosts.filter(g => g.level === Number(filterLevel.value));

        if (filterType.value) {
          newGhosts = newGhosts.filter(g => g.type === filterType.value);
        }

        if (filterName.value) {
          newGhosts = newGhosts.filter(ghost =>
            ghost.name.toLowerCase().includes(filterName.value.toLowerCase())
          );
        }

        newGhosts.sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt));

        hasMore.value = false;

        if (isLoadMore && !reset) {
          ghosts.value = [...ghosts.value, ...newGhosts];
        } else {
          ghosts.value = newGhosts;
          currentPage.value = 1;
        }
      } catch (error) {
        console.error('Error fetching ghosts:', error);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลวิญญาณ';
      } finally {
        isLoading.value = false;
      }
    };

    // Compute displayed ghosts for pagination
    const displayedGhosts = computed(() => {
      const start = (currentPage.value - 1) * ghostsPerLoad;
      const end = start + ghostsPerLoad;
      return ghosts.value.slice(start, end);
    });

    // Apply filters and reset
    const applyFilters = () => {
      ghosts.value = [];
      lastVisible.value = null;
      hasMore.value = true;
      fetchGhosts(false, true);
    };

    // Debounced version of applyFilters for name search
    const debouncedApplyFilters = debounce(applyFilters, 300);

    // Clear all filters
    const clearFilters = () => {
      filterName.value = '';
      filterLevel.value = '';
      filterType.value = '';
      applyFilters();
    };

    // Load more ghosts
    const loadMore = () => {
      if (currentPage.value * ghostsPerLoad >= ghosts.value.length && hasMore.value) {
        fetchGhosts(true);
      }
      currentPage.value += 1;
    };

    // Reset and fetch ghosts
    const resetAndFetch = () => {
      ghosts.value = [];
      lastVisible.value = null;
      hasMore.value = true;
      currentPage.value = 1;
      fetchGhosts();
    };

    // Open edit modal
    const openEditModal = (ghost) => {
      selectedGhost.value = { ...ghost };
      toggleModal.value = 'EditGhostModal';
    };

    // Open details modal
    const openDetailsModal = (ghost) => {
      selectedGhost.value = { ...ghost };
      toggleModal.value = 'GhostDetailsModal';
    };

    onMounted(() => {
      // admin status is now reactive via store watch
    });

    return { 
      toggleModal, 
      filterName, 
      filterLevel, 
      filterType, 
      ghosts, 
      displayedGhosts, 
      selectedGhost, 
      openEditModal, 
      openDetailsModal, 
      fetchGhosts, 
      loadMore, 
      hasMore, 
      isLoading, 
      isAdmin, 
      resetAndFetch, 
      applyFilters, 
      debouncedApplyFilters, 
      clearFilters, 
      error, 
      handleImageError 
    };
  },
};
</script>

<style scoped>
.min-h-screen {
  min-height: 100vh;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

button:disabled, input:disabled, select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}

.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fcondo.png?alt=media&token=198ebb1c-66e5-4bf0-aa79-32bff287aa09') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
}
</style>
